// hooks/useUrlParams.ts
import { useQueryParams } from '@/hooks';
import { DataTablePageSizeEnum } from '@/enums';
import { TViewMode } from '..';
import { useCallback } from 'react';

/**
 * Custom hook to synchronize UI state (filters, pagination, sorting) with URL parameters.
 * It ensures type safety and performs atomic updates to prevent unnecessary re-renders.
 */
const useUrlParams = () => {
  const {
    values: [
      sortType, sortBy, limit, page, gender, 
      view, searchQuery, accountStatus, certified, transactionId
    ],
    setValue,
    deleteValue,
    appendValue,
    updateSearchParams
  } = useQueryParams(
    // 1. List of keys to observe in the URL
    ['sortType', 'sortBy', 'limit', 'page', 'gender', 'view', 'searchQuery', 'accountStatus', 'certified', 'transactionId'],
    // 2. Default fallback values when parameters are missing from the URL
    {
      limit: DataTablePageSizeEnum._12,
      view: 'grid' as TViewMode,
      page: 1
    }
  );

  /**
   * Safely parses multi-value parameters (e.g., ?gender=male&gender=female).
   * Ensures the output is always a clean string array, regardless of URL input format.
   */
  const parseMultiValues = useCallback((value: unknown): string[] => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string' && value) return [value];
    return [];
  }, []);

  /**
   * Performs an atomic update for array-based parameters.
   * Clears existing values and appends new ones in a single URL transition
   * to avoid multiple browser history entries and re-renders.
   */
  const setArrayParam = useCallback((key: 'gender' | 'accountStatus', values: string[]) => {
    updateSearchParams(l => {
      l.delete(key);
      values.forEach(value => l.append(key, value));
      return l;
    });
  }, [updateSearchParams]);

  /**
   * Batch deletes multiple URL parameters in a single operation.
   */
  const deleteMultipleValues = useCallback((keys: string[]) => {
    updateSearchParams(l => {
      keys.forEach(key => l.delete(key));
      return l;
    });
  }, [updateSearchParams]);

  return {
    // --- Data (Cast to specific domain types) ---
    limit: limit as DataTablePageSizeEnum,
    view: view as TViewMode,
    searchQuery,
    accountStatus: parseMultiValues(accountStatus),
    certified,
    transactionId,
    sortType,
    gender: parseMultiValues(gender),
    sortBy,
    page,

    // --- Actions ---
    setValue,
    deleteValue,
    appendValue,
    setArrayParam,
    deleteMultipleValues,
    updateSearchParams
  };
};

export default useUrlParams;
